import * as WebLN from "webln"

import { WebLNProvider } from "webln"
import { SbDispatch } from "./reducer/reducer"

const WEBLN_LOCALSTORAGE_KEY = "webln-approval"
const WEBLN_LOCALSTORAGE_APPROVED = "approved"
const WEBLN_LOCALSTORAGE_REJECTED = "rejected"

/**
 * Functions for querying our interaction with localstorage
 * in the browser. We use this for tracking if we've asked
 * the user or not for WebLN access. Prompting the user too
 * many times is poor UX, and should be avoided.
 */
export const WebLNLocalStorage = {
  userHasApproved: (): boolean => {
    const webLnLocalStorage = window.localStorage.getItem(
      WEBLN_LOCALSTORAGE_KEY
    )
    return webLnLocalStorage === WEBLN_LOCALSTORAGE_APPROVED
  },
  userHasRejected: (): boolean => {
    const webLnLocalStorage = window.localStorage.getItem(
      WEBLN_LOCALSTORAGE_KEY
    )
    return webLnLocalStorage === WEBLN_LOCALSTORAGE_REJECTED
  },
  userHasBeenAsked: (): boolean =>
    window.localStorage.getItem(WEBLN_LOCALSTORAGE_KEY) !== null
}

/**
 * Requests access to the users WebLN installation.
 * Logs the result in localstorage, so we avoid
 * prompting multiple times.
 *
 * @param dispatch Dispatch function that gives access
 * to the reducer
 */
export const RequestWebLn: (
  dispatch: SbDispatch
) => Promise<void> = async dispatch => {
  try {
    const ln = await WebLN.requestProvider()
    dispatch({ type: "SET_WEBLN", webLn: ln })
    window.localStorage.setItem(
      WEBLN_LOCALSTORAGE_KEY,
      WEBLN_LOCALSTORAGE_APPROVED
    )
  } catch (err) {
    // tslint:disable-next-line: no-console
    console.error("WebLN.requestProvider error:", err)
    if ((err as Error).message.includes("User rejected prompt")) {
      window.localStorage.setItem(
        WEBLN_LOCALSTORAGE_KEY,
        WEBLN_LOCALSTORAGE_REJECTED
      )
    }
  }
}

interface RequestInvoiceArgs {
  /**
   * Dispatch function that gives access to the root reducer
   */
  dispatch: SbDispatch

  /**
   * The users WebLN provider
   */
  ln: WebLNProvider
}

/**
 * Asks the user for an invoice we can use as a refund invoice
 * for subscriptions
 */
export const RequestInvoice: (
  args: RequestInvoiceArgs
) => Promise<void> = async ({ dispatch, ln }) => {
  try {
    const { paymentRequest } = await ln.makeInvoice({
      amount: "0",
      defaultMemo: "Refund payment for Suredbits API subscription"
    })
    dispatch({ type: "SET_REFUND_INVOICE", invoice: paymentRequest })
  } catch (err) {
    // tslint:disable-next-line: no-console
    console.error("Error when requesting invoice:", err)
  } finally {
    // to avoid spamming the user
    dispatch({ type: "ASKED_FOR_INVOICE" })
  }
}

interface SendPaymentArgs {
  /**
   * The users WebLN provider
   */
  ln: WebLNProvider

  /**
   * The invoice to pay
   */
  invoice: string
}

/**
 * Prompts thet user to pay an invoice
 */
export const SendPayment: (args: SendPaymentArgs) => Promise<string> = async ({
  ln,
  invoice
}) => {
  try {
    const res = await ln.sendPayment(invoice)
    return res.preimage
  } catch (err) {
    // tslint:disable-next-line: no-console
    console.error("Error when paying invoice: %O", err)
    throw err
  }
}
